import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Header } from '../../../../components/organisms/Header/Header';
import { PageView, UserInteractionEvent } from '../../../../models/analytics';

import { H1 } from '../../../../components/atoms/Heading/Heading';
import { Footer } from '../../../../components/atoms/Footer/Footer';
import { Button } from '../../../../components/atoms/Button/Button';
import { ContentBody } from '../../../../components/atoms/ContentBody/ContentBody';
import { ScreenWrapper } from '../../../../components/atoms/ScreenWrapper/ScreenWrapper';
import { GradientList } from '../../../../components/molecules/GradientList/GradientList';
import { commonMessages } from '../../../../components/atoms/CommonMessages/messages';
import { Subtitle } from '../../../../components/atoms/Subtitle/Subtitle';
import { usePageViewEvents } from '../../../../core/Analytics/usePageViewEvents';
import { AccountListItem, ListAccount } from './atoms/AccountListItem';

export type AccountListProps = {
  accounts: ListAccount[];
  title: JSX.Element;
  subTitle?: JSX.Element;
  onCancel: () => void;
  providerDisplayName: string;
  allowSelectMultipleAccounts: boolean;
  onSubmit: (accountIds: string[]) => void;
};

export const AccountList: React.FC<AccountListProps> = ({
  title,
  subTitle,
  accounts,
  providerDisplayName,
  onCancel,
  onSubmit,
  allowSelectMultipleAccounts,
}) => {
  const pageView = PageView.ACCOUNT_SELECTION_SCREEN;
  const { sendViewInteractionEvent } = usePageViewEvents({ pageView });
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);

  return (
    <ScreenWrapper>
      <Header addBottomMargin onBack={onCancel} pageView={pageView} />
      <ContentBody>
        <H1>{title}</H1>
        {subTitle && <Subtitle mb="medium">{subTitle}</Subtitle>}

        <GradientList>
          {accounts.map((account) => {
            const selected = selectedAccounts.includes(account.id);

            return (
              <AccountListItem
                key={account.id}
                highlighted={selected}
                account={account}
                showDetails={selected}
                providerDisplayName={providerDisplayName}
                onClick={(accountId: string) => {
                  let newSet;

                  if (selected) {
                    newSet = new Set(selectedAccounts);
                    newSet.delete(accountId);
                  } else {
                    newSet = allowSelectMultipleAccounts
                      ? new Set(selectedAccounts).add(accountId)
                      : new Set([accountId]);
                  }

                  setSelectedAccounts(Array.from(newSet));
                }}
              />
            );
          })}
        </GradientList>
        <Footer stickToBottom>
          <Button
            color="primary"
            type="button"
            variant="contained"
            data-testid="continue-button"
            disabled={selectedAccounts.length === 0}
            onClick={() => {
              sendViewInteractionEvent({ interaction: UserInteractionEvent.PRIMARY_BUTTON });
              onSubmit(selectedAccounts);
            }}
          >
            <FormattedMessage {...commonMessages.continue} />
          </Button>
        </Footer>
      </ContentBody>
    </ScreenWrapper>
  );
};
